import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getProfile, updateVDOT, updatePacesVisibility } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Activity, Eye, EyeOff, Info } from 'lucide-react';


function VDOTConfig() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    distance: '',
    heures: '',
    minutes: '',
    secondes: ''
  });
  const [pacesVisibles, setPacesVisibles] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [isAuthenticated, navigate]);

  const fetchProfile = async () => {
    try {
      const data = await getProfile();
      setProfile(data);
      setPacesVisibles(data.pacesVisibles || false);
      
      // Pré-remplir si des données existent
      if (data.tempsReference && data.tempsReference.distance) {
        const temps = data.tempsReference.temps;
        const heures = Math.floor(temps / 3600);
        const minutes = Math.floor((temps % 3600) / 60);
        const secondes = temps % 60;
        
        setFormData({
          distance: data.tempsReference.distance,
          heures: heures.toString(),
          minutes: minutes.toString(),
          secondes: secondes.toString()
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
  if (!formData.distance || (!formData.minutes && !formData.heures)) {
    alert('Veuillez remplir tous les champs requis');
    return;
  }

  // Convertir en secondes
  const heures = parseInt(formData.heures || 0);
  const minutes = parseInt(formData.minutes || 0);
  const secondes = parseInt(formData.secondes || 0);
  const tempsTotal = heures * 3600 + minutes * 60 + secondes;

  if (tempsTotal <= 0) {
    alert('Veuillez entrer un temps valide');
    return;
  }

  setLoading(true);
  try {
    const updated = await updateVDOT(formData.distance, tempsTotal); // ← ICI, tempsTotal en secondes
    setProfile(updated);
    //alert('VDOT et paces calculés avec succès !');
  } catch (error) {
    console.error('Erreur:', error);
    //alert(error.response?.data?.message || 'Erreur lors du calcul');
  } finally {
    setLoading(false);
  }
};

  const handleVisibilityToggle = async () => {
    try {
      const newVisibility = !pacesVisibles;
      await updatePacesVisibility(newVisibility);
      setPacesVisibles(newVisibility);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const formatPace = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTempsReference = (seconds) => {
    if (!seconds) return '--:--';
    const heures = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.round(seconds % 60);
    
    if (heures > 0) {
      return `${heures}h${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-center mb-6">
            <Activity className="text-indigo-600 mr-3" size={36} />
            <h2 className="text-3xl font-bold text-gray-800">Configuration VDOT</h2>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <div className="flex">
              <Info className="text-blue-500 mr-3 flex-shrink-0" size={24} />
              <div>
                <p className="text-sm text-gray-700">
                  Entrez votre meilleur temps récent sur une distance pour calculer automatiquement 
                  vos allures d'entraînement selon la méthode VDOT de Jack Daniels.
                </p>
                {profile && profile.vdot && (
                  <p className="text-sm text-indigo-700 font-semibold mt-2">
                    ℹ️ Vous pouvez recalculer votre VDOT à tout moment avec un nouveau temps de référence.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Formulaire temps de référence */}
          <div className="space-y-6">
            {profile && profile.tempsReference && profile.tempsReference.distance && (
              <div className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-800 mb-2">📊 Temps de référence actuel</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Distance</p>
                    <p className="text-lg font-bold text-gray-800">{profile.tempsReference.distance}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Temps</p>
                    <p className="text-lg font-bold text-gray-800">
                      {formatTempsReference(profile.tempsReference.temps)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">VDOT</p>
                    <p className="text-2xl font-bold text-indigo-600">{profile.vdot}</p>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Distance de référence *
              </label>
              <select
                name="distance"
                value={formData.distance}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Sélectionner une distance...</option>
                <option value="400m">400m</option>
                <option value="800m">800m</option>
                <option value="1500m">1500m</option>
                <option value="mile">Mile (1609m)</option>
                <option value="3km">3km</option>
                <option value="2miles">2 miles (3219m)</option>
                <option value="5km">5km</option>
                <option value="8km">8km</option>
                <option value="5miles">5 miles (8047m)</option>
                <option value="10km">10km</option>
                <option value="15km">15km</option>
                <option value="10miles">10 miles (16093m)</option>
                <option value="20km">20km</option>
                <option value="semi">Semi-marathon (21,1 km)</option>
                <option value="25km">25km</option>
                <option value="30km">30km</option>
                <option value="marathon">Marathon (42,2 km)</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-2">
                Temps réalisé *
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Heures</label>
                  <input
                    type="number"
                    name="heures"
                    value={formData.heures}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Minutes *</label>
                  <input
                    type="number"
                    name="minutes"
                    value={formData.minutes}
                    onChange={handleChange}
                    min="0"
                    max="59"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Secondes</label>
                  <input
                    type="number"
                    name="secondes"
                    value={formData.secondes}
                    onChange={handleChange}
                    min="0"
                    max="59"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition font-semibold disabled:opacity-50"
            >
              {loading ? 'Calcul en cours...' : 'Calculer mes paces'}
            </button>
          </div>

          {/* Affichage des résultats */}
          {profile && profile.vdot && (
            <div className="mt-8 pt-8 border-t">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Mes allures d'entraînement</h3>
                <button
                  onClick={handleVisibilityToggle}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition ${
                    pacesVisibles 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pacesVisibles ? <Eye size={20} /> : <EyeOff size={20} />}
                  <span className="font-semibold">
                    {pacesVisibles ? 'Visibles publiquement' : 'Privées'}
                  </span>
                </button>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-6 mb-6">
                <p className="text-center text-gray-700">
                  Votre <span className="font-bold text-indigo-600 text-2xl">VDOT: {profile.vdot}</span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <h4 className="font-semibold text-gray-800 mb-2">Endurance Fondamentale</h4>
                  <p className="text-2xl font-bold text-green-700">
                    {formatPace(profile.paces.easyMin)} - {formatPace(profile.paces.easyMax)} /km
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Pour les sorties faciles et récupération</p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <h4 className="font-semibold text-gray-800 mb-2">Allure Marathon</h4>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatPace(profile.paces.marathon)} /km
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Pour les sorties longues</p>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                  <h4 className="font-semibold text-gray-800 mb-2">Seuil / Tempo</h4>
                  <p className="text-2xl font-bold text-yellow-700">
                    {formatPace(profile.paces.threshold)} /km
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Pour les séances au seuil (20-60 min)</p>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded">
                  <h4 className="font-semibold text-gray-800 mb-2">Fractionné Long (VMA)</h4>
                  <p className="text-2xl font-bold text-orange-700">
                    {formatPace(profile.paces.interval)} /km
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Pour les intervalles 3-5 min</p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded md:col-span-2">
                  <h4 className="font-semibold text-gray-800 mb-2">Fractionné Court (VMA courte)</h4>
                  <p className="text-2xl font-bold text-red-700">
                    {formatPace(profile.paces.repetition)} /km
                  </p>
                  <p className="text-sm text-gray-600 mt-1">Pour les répétitions courtes (200m-800m)</p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex gap-4">
            <button
              onClick={() => navigate('/profile')}
              className="flex-1 bg-gray-300 text-gray-800 py-3 rounded-lg hover:bg-gray-400 transition font-semibold"
            >
              Retour au profil
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

export default VDOTConfig;